import { produce } from 'immer'
import {
  SAVE_SELECTED_VIRTUAL_CARD_IDS,
  SaveSelectedVirtualCardIdsAction,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import { BulkUploadResponse } from 'src/models/bulkUpload.models'
import { UserConnectionResponse } from 'src/models/people.models'
import {
  VirtualCardListResponse,
  VirtualCardSecureData,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsResponse } from 'src/models/virtualCardPermissions.models'
import { JobStatus } from 'src/models/virtualCardPushTask.models'
import {
  GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS,
  GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS,
  GET_VIRTUAL_CARD_SUCCESS,
  GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS,
  GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS,
  GET_VIRTUAL_CARDS_SUCCESS,
  GetVirtualCardActions,
  GetVirtualCardPermissionsInBulkActions,
  GetVirtualCardPermissionsSuccessAction,
  GetVirtualCardsStatisticsClearedTransactionCreditAmountActions,
  GetVirtualCardsStatisticsClearedTransactionDebitAmountActions,
  GetVirtualCardsSuccessAction,
  POST_BULK_VIRTUAL_CARD_COMPLETION,
  POST_BULK_VIRTUAL_CARD_SUCCESS,
  POST_VIRTUAL_CARD_SUCCESS,
  PostBulkVirtualCardActions,
  PostVirtualCardSuccessAction,
  PUT_CLOSE_VIRTUAL_CARD_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  PUT_VIRTUAL_CARD_REJECT_SUCCESS,
  PUT_VIRTUAL_CARD_SUCCESS,
  PutCloseVirtualCardSuccessAction,
  PutVirtualCardCancelSuccessAction,
  PutVirtualCardCancelUpdateSuccessAction,
  PutVirtualCardRejectSuccessAction,
  PutVirtualCardSuccessAction,
} from 'src/redux/actions/data/virtualCards.actions'

export interface VirtualCardsState {
  listResponses: Record<string, VirtualCardListResponse>
  bulkJobs: Record<string, BulkUploadResponse>
  secureData: Record<string, VirtualCardSecureData>
  permissions: Record<string, VirtualCardPermissionsResponse>
  cardCreator?: UserConnectionResponse
  clearedTransactionDebitAmount: Record<string, number>
  clearedTransactionCreditAmount: Record<string, number>
  selectedVirtualCardIds?: string[]
}

export const initialState: VirtualCardsState = {
  listResponses: {},
  bulkJobs: {},
  secureData: {},
  permissions: {},
  clearedTransactionDebitAmount: {},
  clearedTransactionCreditAmount: {},
}

export type Actions =
  | GetVirtualCardPermissionsSuccessAction
  | GetVirtualCardActions
  | PostVirtualCardSuccessAction
  | PutVirtualCardCancelSuccessAction
  | PutCloseVirtualCardSuccessAction
  | PutVirtualCardCancelUpdateSuccessAction
  | PutVirtualCardRejectSuccessAction
  | PutVirtualCardSuccessAction
  | GetVirtualCardsSuccessAction
  | PostBulkVirtualCardActions
  | GetVirtualCardsStatisticsClearedTransactionDebitAmountActions
  | GetVirtualCardsStatisticsClearedTransactionCreditAmountActions
  | SaveSelectedVirtualCardIdsAction
  | GetVirtualCardPermissionsInBulkActions

const VirtualCardsReducer = (
  state: VirtualCardsState = initialState,
  action: Actions,
): VirtualCardsState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS: {
        draft.permissions = {
          ...draft.permissions,
          [action.payload.virtualCardId]: action.payload,
        }
        return draft
      }

      case GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS: {
        draft.permissions = action.payload.data.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.virtualCardId]: curr,
          }),
          draft.permissions,
        )
        return draft
      }

      case GET_VIRTUAL_CARD_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.virtualCard.id]: action.payload.virtualCard,
        }
        draft.secureData = {
          ...draft.secureData,
          [action.payload.virtualCard.id]: {
            vcn: action.payload.virtualCard.vcn,
            securityCode: action.payload.virtualCard.securityCode,
            receiptAttachments: action.payload.virtualCard.receiptAttachments,
          },
        }
        return draft
      }

      case PUT_VIRTUAL_CARD_CANCEL_SUCCESS:
      case PUT_CLOSE_VIRTUAL_CARD_SUCCESS:
      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS:
      case PUT_VIRTUAL_CARD_REJECT_SUCCESS:
      case PUT_VIRTUAL_CARD_SUCCESS:
      case POST_VIRTUAL_CARD_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.virtualCard.id]: action.payload.virtualCard,
        }
        draft.secureData = {
          // implicit knowledge here that these will NOT return card numbers
          ...draft.secureData,
          [action.payload.virtualCard.id]: {
            ...(draft.secureData[action.payload.virtualCard.id] || {}),
            receiptAttachments: action.payload.virtualCard.receiptAttachments,
          },
        }
        return draft
      }

      case POST_BULK_VIRTUAL_CARD_SUCCESS: {
        draft.bulkJobs = {
          ...draft.bulkJobs,
          [action.payload.bulkVirtualCardPush.bulkVirtualCardUploadId]: {
            id: action.payload.bulkVirtualCardPush.bulkVirtualCardUploadId,
            tasks: action.payload.bulkVirtualCardPush.tasks,
            displayName: action.payload.displayName,
            status: JobStatus.Running,
            invitingUsers: action.payload.invitingUsers,
          },
        }
        return draft
      }

      case POST_BULK_VIRTUAL_CARD_COMPLETION: {
        draft.bulkJobs = {
          ...draft.bulkJobs,
          [action.payload]: {
            ...draft.bulkJobs[action.payload],
            status: JobStatus.Completed,
          },
        }
        return draft
      }

      case GET_VIRTUAL_CARDS_SUCCESS: {
        draft.listResponses = action.payload.virtualCards.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          draft.listResponses,
        )
        return draft
      }

      case GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS: {
        draft.clearedTransactionDebitAmount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.virtualCardIds[index]]: curr.value,
          }),
          draft.clearedTransactionDebitAmount,
        )
        return draft
      }

      case GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS: {
        draft.clearedTransactionCreditAmount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.virtualCardIds[index]]: curr.value,
          }),
          draft.clearedTransactionCreditAmount,
        )
        return draft
      }

      case SAVE_SELECTED_VIRTUAL_CARD_IDS: {
        draft.selectedVirtualCardIds = action.payload.selectedVirtualCardIds
        return draft
      }
    }
  })
}

export default VirtualCardsReducer
